import { ResponsiveLine } from "@nivo/line";
import { useMemo } from "react";
import { uniq } from "lodash";
import { isDefined } from "react-migration/lib/util/isDefined";
import clsx from "clsx";
import { formatNumberThousands, useTrafficCountColorAccessor } from "../util";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useArrayToggle } from "react-migration/lib/util/useArrayToggle";
import { Legend, ChartTooltip } from "react-migration/components/Charts";

const CHART_HEIGHT = 200;
const TICK_SIZE = 5;
const TICK_PADDING = 5;
const TEXT_HEIGHT = 11;
const ROTATED_TEXT_HEIGHT = 30;

const MAX_Y_TICKS = 5;

const textLengthX = 25;

const marginBottom = TICK_SIZE + TICK_PADDING + ROTATED_TEXT_HEIGHT;

const marginTop = TEXT_HEIGHT / 2;
const marginRight = textLengthX / 2;

type TrafficCountChartData = {
  id: string;
  data: {
    x: number | string;
    y: number | string;
  }[];
};

type TrafficCountChartProps = {
  data: TrafficCountChartData[];
  printable?: boolean;
};

export const TrafficCountChart = ({ data, printable = false }: TrafficCountChartProps) => {
  const { t } = useTranslation();
  const colorAccessor = useTrafficCountColorAccessor();

  const [hiddenSeries, toggleSeriesById] = useArrayToggle(["all_motor_vehicles"]);

  const allSeries = useMemo(
    () =>
      uniq(data)
        .filter(isDefined)
        .map(({ id, data }) => ({
          id,
          serieId: id,
          color: colorAccessor(id),
          display_name: t(`sidebar.selection.traffic_counts.vehicle_types.${id}`),
          data: data
            .map(({ x, y }) => ({
              x: x,
              y: y,
            }))
            .filter(({ y }) => isDefined(y) || y === 0 || y === "0")
            .sort((a, b) => (a.x as number) - (b.x as number)),
        }))
        .filter(({ data }) => !!data.length),
    [colorAccessor, data, t]
  );

  const visibleSeries = useMemo(
    () => allSeries.filter(({ id }) => !hiddenSeries.includes(id)),
    [allSeries, hiddenSeries]
  );

  const years = visibleSeries.length > 0 ? visibleSeries[0].data.map((d) => d.x as number) : [];
  const uniqueYears = [...new Set(years)].sort((a, b) => a - b);

  const totalHeight = marginTop + CHART_HEIGHT + marginBottom;

  return (
    <div className="flex flex-col w-full gap-4 grow">
      <div
        className={clsx("relative print:justify-self-start print:origin-left", {
          "w-[335px]": printable,
        })}
        style={{
          height: `${totalHeight}px`,
        }}
      >
        <ResponsiveLine
          data={visibleSeries}
          margin={{ bottom: marginBottom, left: 32, top: marginTop, right: marginRight }}
          isInteractive={true}
          curve={"cardinal"}
          tooltip={({ point }) => {
            return <ChartTooltip value={point} color={point.serieColor} />;
          }}
          xScale={{
            type: "linear",
            min: "auto",
            max: "auto",
          }}
          yScale={{
            min: "auto",
            max: "auto",
            type: "linear",
            nice: true,
            stacked: false,
            reverse: false,
          }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: TICK_SIZE,
            tickValues: uniqueYears,
            tickRotation: -45,
          }}
          axisLeft={{
            tickSize: TICK_SIZE,
            tickPadding: TICK_PADDING,
            tickValues: MAX_Y_TICKS,
            format: formatNumberThousands,
          }}
          colors={(d) => d.color}
          enableGridX={false}
          enablePoints={false}
          useMesh={true}
          theme={{
            axis: {
              ticks: { text: { fill: "#6d7482" } },
              domain: {
                line: {
                  stroke: "#e5e7eb",
                  strokeWidth: 2,
                },
              },
            },
          }}
          pointSize={6}
          pointColor={"white"}
          pointBorderWidth={2}
          pointBorderColor={{ from: "serieColor" }}
        />
      </div>
      <Legend
        series={allSeries}
        hiddenSeries={hiddenSeries}
        toggleSeriesById={toggleSeriesById}
        printable={printable}
        className="grid grid-cols-3 pl-4"
        direction="row"
      />
    </div>
  );
};
